import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { IdleManager } from "tools/idle";
import { apiV2 } from "api/firebase";

import {
  INotificationBaseRelatedToShow,
  ISignature,
  NotificationType,
} from "@evennt-tech/api-v2-core";

import {
  ICreatePublicSignatureReq,
  ICreatePublicSignatureRes,
  IGetAllPublicSignatureReq,
} from "@evennt-tech/api-v2-external";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  publicItems: Record<string, Array<ISignature>>;
  isLoading: boolean;
};

type Action = {
  fetchPublic: (data: IGetAllPublicSignatureReq) => Promise<void>;
  updatePublic: (data: IGetAllPublicSignatureReq) => Promise<void>;
  autoCatchPublic: (data: IGetAllPublicSignatureReq) => Promise<void>;
  createPublic: (
    body: ICreatePublicSignatureReq
  ) => Promise<ICreatePublicSignatureRes | undefined>;
  getPublicByShowId: (showId: string) => Array<ISignature>;
};

export const usePublicSignatures = create<State & Action>()(
  devtools((set, get) => ({
    publicItems: {} as Record<string, Array<ISignature>>,
    isLoading: false as boolean,

    getPublicByShowId: (showId) => get().publicItems[showId] || [],

    autoCatchPublic: async (data) => {
      const { updatePublic } = get();
      const { showId } = data;

      IdleManager.getInstance().addSubscriber(
        `signature-public-${showId}`,
        async () => {
          await updatePublic(data);
        }
      );

      const keys = [
        NotificationType.CREATED_SIGNATURE,
        NotificationType.UPDATED_SIGNATURE,
        NotificationType.DELETED_SIGNATURE,
      ];

      for (const key of keys) {
        apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
          key,
          showId,
          callback: async () => {
            await updatePublic(data);
          },
        });
      }
    },

    updatePublic: async (data) => {
      const { publicItems } = get();
      const { showId } = data;
      try {
        const signatures = await apiV2.signature.signaturePublic.getAll(data);

        set({ publicItems: { ...publicItems, [showId]: signatures } });
      } catch (e) {
        console.error("public signature store update:", e);
      }
    },

    fetchPublic: async (data) => {
      const { publicItems, isLoading, updatePublic } = get();
      const { showId } = data;

      if (publicItems[showId] || isLoading) return;

      set({ isLoading: true });
      try {
        await updatePublic(data);
      } catch (e) {
        console.error("public signature store fetch:", e);
      } finally {
        set({ isLoading: false });
      }
    },

    createPublic: async (body) => {
      set({ isLoading: true });
      try {
        const res = await apiV2.signature.signaturePublic.create(body);
        // Mirror the organiser store: only library signatures
        // (`shouldBeShownInList: true`) belong in the reuse cache. A
        // verdict-time dummy (saveSignature=false) is referenced only by
        // `task_signatures` and must not leak into any reuse picker.
        if (res.shouldBeShownInList) {
          const current = get().publicItems[body.showId] || [];
          set((state) => ({
            publicItems: {
              ...state.publicItems,
              [body.showId]: [...current, res],
            },
          }));
        }
        set({ isLoading: false });
        return res;
      } catch (error) {
        set({ isLoading: false });
        console.error("Failed to create public signature:", error);
      }
    },
  }))
);

registerStore(usePublicSignatures);
